import { ColumnDef, SortingState } from "@tanstack/react-table";
import React, { useCallback, useMemo } from "react";
import { DataTable } from "../../../components/data-table/DataTable";
import { useLocalization } from "../../../hooks/useLocalization";
import type { UserUsageEntry } from "./types";
import { formatDurationMinutes, resolveUserDisplayName } from "./utils";

export const INITIAL_SORTING: SortingState = [
  { id: "sessionCount", desc: true },
];

interface DisplayRow {
  id: string;
  user: string;
  email: string;
  sessionCount: number;
  uniqueExperienceCount: number;
  durationDisplay: string;
  totalDurationSeconds: number;
  _original: UserUsageEntry;
}

interface UserUsageTableProps {
  data: UserUsageEntry[];
  isLoading?: boolean;
  userDisplayNames?: Map<string, string>;
  userEmails?: Map<string, string>;
  onSortingChange?: (sorting: SortingState) => void;
  onFilteredDataChange?: (rows: UserUsageEntry[]) => void;
  filterSlot?: React.ReactNode;
}

export const UserUsageTable: React.FC<UserUsageTableProps> = ({
  data,
  isLoading = false,
  userDisplayNames,
  userEmails,
  onSortingChange,
  onFilteredDataChange,
  filterSlot,
}) => {
  const { t } = useLocalization();

  const displayRows = useMemo<DisplayRow[]>(
    () =>
      data.map((entry) => ({
        id: entry.id,
        user: resolveUserDisplayName(entry.userId, userDisplayNames),
        email: userEmails?.get(entry.userId) ?? "",
        sessionCount: entry.sessionCount,
        uniqueExperienceCount: entry.uniqueExperienceCount,
        durationDisplay: formatDurationMinutes(entry.totalDurationSeconds),
        totalDurationSeconds: entry.totalDurationSeconds,
        _original: entry,
      })),
    [data, userDisplayNames, userEmails],
  );

  const columns = useMemo<ColumnDef<DisplayRow, any>[]>(
    () => [
      {
        id: "user",
        header: t("pages.dataAnalytics.userLevelData.userUsage.columns.user"),
        accessorKey: "user",
        cell: (ctx) => ctx.getValue<string>(),
        enableSorting: true,
        enableColumnFilter: false,
        meta: { mobileTitle: true },
      },
      {
        id: "email",
        header: t("pages.dataAnalytics.userLevelData.userUsage.columns.email"),
        accessorKey: "email",
        cell: (ctx) => ctx.getValue<string>() || "—",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        id: "sessionCount",
        header: t(
          "pages.dataAnalytics.userLevelData.userUsage.columns.sessions",
        ),
        accessorKey: "sessionCount",
        cell: (ctx) => ctx.getValue<number>(),
        enableSorting: true,
        enableColumnFilter: false,
        sortingFn: "basic",
        size: 80,
      },
      {
        id: "uniqueExperiences",
        header: t(
          "pages.dataAnalytics.userLevelData.userUsage.columns.uniqueExperiences",
        ),
        accessorKey: "uniqueExperienceCount",
        cell: (ctx) => ctx.getValue<number>(),
        enableSorting: true,
        enableColumnFilter: false,
        sortingFn: "basic",
        size: 80,
      },
      {
        id: "duration",
        header: t(
          "pages.dataAnalytics.userLevelData.userUsage.columns.duration",
        ),
        accessorKey: "totalDurationSeconds",
        cell: (ctx) => ctx.row.original.durationDisplay,
        enableSorting: true,
        enableColumnFilter: false,
        sortingFn: "basic",
        size: 80,
      },
    ],
    [t],
  );

  const getRowId = useCallback((row: DisplayRow) => row.id, []);

  const handleFilteredDataChange = useCallback(
    (rows: DisplayRow[]) => {
      onFilteredDataChange?.(rows.map((r) => r._original));
    },
    [onFilteredDataChange],
  );

  return (
    <DataTable
      columns={columns}
      data={displayRows}
      defaultSort={INITIAL_SORTING}
      filterSlot={filterSlot}
      getRowId={getRowId}
      globalFilterPlaceholder={t(
        "pages.dataAnalytics.userLevelData.userUsage.searchPlaceholder",
      )}
      isLoading={isLoading}
      onFilteredDataChange={handleFilteredDataChange}
      onSortingChange={onSortingChange}
      showActionButton={false}
      tableKey="userUsage"
      virtualize={true}
    />
  );
};
